import { useCallback, useEffect, useMemo, useRef } from "react";
import type {
	ColDef,
	FirstDataRenderedEvent,
	GridApi,
	RowClickedEvent,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import { useArrowRowNavigation } from "@/controls/ag-grid/useArrowRowNavigation";
import {
	NUMBER_CELL_CLASS,
	ONE_LINE_CELL_CLASS,
	OneLineCellStyles,
} from "@/controls/ag-grid/OneLineCells";
import type { RollsetListItem } from "../models/RollshopOperations";
import {
	compareRollsetNames,
	formatDiameter,
	getRollsetPositions,
	rollsOf,
} from "../models/Rollshop";

const diametersOf = (row: RollsetListItem) =>
	getRollsetPositions(row)
		.map((p) => formatDiameter(p.diameter))
		.join(" - ");

interface Props {
	rows: RollsetListItem[];
	selectedId: number | null;
	/** Changes on each pick in the search: the list scrolls to the selected row and focuses it. */
	revealKey: number;
	onRowSelected: (row: RollsetListItem) => void;
}

export const RollshopTable = ({
	rows,
	selectedId,
	revealKey,
	onRowSelected,
}: Props) => {
	const columnDefs = useMemo<ColDef<RollsetListItem>[]>(
		() => [
			{
				field: "Name",
				headerName: "Name",
				width: 120,
				sort: "asc",
				comparator: (a: string | null, b: string | null) =>
					compareRollsetNames(a ?? "", b ?? ""),
				cellClass: ONE_LINE_CELL_CLASS,
			},
			{
				colId: "Rolls",
				headerName: "Rolls",
				flex: 1.2,
				minWidth: 170,
				valueGetter: (p) => (p.data ? rollsOf(p.data) : ""),
				tooltipValueGetter: (p) => p.value,
				cellClass: ONE_LINE_CELL_CLASS,
			},
			{
				colId: "Diameters",
				headerName: "Diameters",
				flex: 1,
				minWidth: 130,
				valueGetter: (p) => (p.data ? diametersOf(p.data) : ""),
				tooltipValueGetter: (p) => p.value,
				cellClass: [ONE_LINE_CELL_CLASS, NUMBER_CELL_CLASS],
			},
			{
				field: "BarrelName",
				headerName: "Barrel",
				flex: 1.4,
				minWidth: 180,
				tooltipField: "BarrelName",
				cellClass: ONE_LINE_CELL_CLASS,
			},
			{
				field: "StatusName",
				headerName: "Op. Status",
				width: 130,
				cellClass: ONE_LINE_CELL_CLASS,
			},
		],
		[],
	);

	const defaultColDef = useMemo(
		() => ({
			...globalDefaultColDef,
			wrapText: false,
			autoHeight: false,
			sortable: true,
			resizable: true,
		}),
		[],
	);

	const onRowClicked = useCallback(
		(event: RowClickedEvent<RollsetListItem>) => {
			if (event.data) onRowSelected(event.data);
		},
		[onRowSelected],
	);

	const onCellKeyDown = useArrowRowNavigation<RollsetListItem>(onRowSelected);

	const gridRef = useRef<AgGridReact<RollsetListItem>>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const selectedIdRef = useRef(selectedId);
	selectedIdRef.current = selectedId;

	const selectedRowIndex = useCallback((api: GridApi<RollsetListItem>) => {
		const id = selectedIdRef.current;
		if (id == null) return null;
		return api.getRowNode(String(id))?.rowIndex ?? null;
	}, []);

	// A link opened with a rollset shows its row, without taking the focus from anyone.
	const onFirstDataRendered = useCallback(
		(event: FirstDataRenderedEvent<RollsetListItem>) => {
			const rowIndex = selectedRowIndex(event.api);
			if (rowIndex != null) event.api.ensureIndexVisible(rowIndex);
		},
		[selectedRowIndex],
	);

	// A pick in the search scrolls to the row and focuses it, so the arrows go on from there.
	// The search box lets go of the focus on the pick, and a row scrolled into view gets its
	// cells a moment later: retry briefly until the browser focus is on that row.
	useEffect(() => {
		const api = gridRef.current?.api;
		if (!revealKey || !api) return;
		const rowIndex = selectedRowIndex(api);
		if (rowIndex == null) return;
		api.ensureIndexVisible(rowIndex);
		const column = api.getFocusedCell()?.column ?? "Name";
		let timer: ReturnType<typeof setTimeout>;
		const focus = (attempt = 0) => {
			// Someone already typing somewhere else keeps their focus.
			if (document.activeElement instanceof HTMLInputElement) return;
			api.setFocusedCell(rowIndex, column);
			const row = document.activeElement?.closest(".ag-row");
			const focused =
				!!row &&
				!!wrapperRef.current?.contains(row) &&
				row.getAttribute("row-index") === String(rowIndex);
			if (!focused && attempt < 10)
				timer = setTimeout(() => focus(attempt + 1), 50);
		};
		timer = setTimeout(focus);
		return () => clearTimeout(timer);
	}, [revealKey, selectedRowIndex]);

	return (
		<div ref={wrapperRef} style={{ height: "100%" }}>
			<OneLineCellStyles />
			<AgGridReact<RollsetListItem>
				ref={gridRef}
				rowData={rows}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				onRowClicked={onRowClicked}
				onCellKeyDown={onCellKeyDown}
				onFirstDataRendered={onFirstDataRendered}
				getRowId={(p) => String(p.data.Id)}
				suppressContextMenu
				alwaysShowVerticalScroll
				animateRows={false}
				rowHeight={28}
				headerHeight={32}
				enableBrowserTooltips
				rowStyle={{ cursor: "pointer" }}
				getRowStyle={(params) =>
					params.data?.Id === selectedId
						? { backgroundColor: "#e3f2fd", color: "inherit" }
						: undefined
				}
			/>
		</div>
	);
};
